import React, { useEffect, useRef, useState, useCallback } from "react";
import { Editor } from "@tiptap/react";
import { BsPencil, BsTrash, BsBoxArrowUpRight } from "react-icons/bs";

interface LinkBubbleProps {
  editor: Editor;
  onEdit: () => void;
}

interface BubbleState {
  visible: boolean;
  href: string;
  top: number;
  left: number;
}

const LinkBubble: React.FC<LinkBubbleProps> = ({ editor, onEdit }) => {
  const [bubble, setBubble] = useState<BubbleState>({
    visible: false,
    href: "",
    top: 0,
    left: 0,
  });
  const bubbleRef = useRef<HTMLDivElement>(null);

  const hideBubble = useCallback(() => {
    setBubble((prev) => ({ ...prev, visible: false }));
  }, []);

  // Handle clicking on links in the editor.
  // Uses CAPTURE phase so we intercept before the browser follows the link.
  useEffect(() => {
    if (!editor) return;

    const editorDom = editor.view.dom;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const linkElement = target.closest("a");

      if (linkElement && editorDom.contains(linkElement)) {
        // Prevent the browser from navigating to the link
        event.preventDefault();
        event.stopImmediatePropagation();

        const href = linkElement.getAttribute("href") || "";
        const rect = linkElement.getBoundingClientRect();
        const editorContainer = editorDom.closest(
          ".tiptap-editor-container"
        );
        const containerRect = editorContainer?.getBoundingClientRect();

        if (containerRect) {
          setBubble({
            visible: true,
            href,
            top: rect.bottom - containerRect.top + 4,
            left: rect.left - containerRect.left,
          });
        }

        // Place cursor at the click position so the link mark is active
        const pos = editor.view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });
        if (pos) {
          editor.commands.setTextSelection(pos.pos);
        }
      }
    };

    // Use capture phase to intercept before browser default behavior
    editorDom.addEventListener("click", handleClick, true);

    return () => {
      editorDom.removeEventListener("click", handleClick, true);
    };
  }, [editor]);

  // Close bubble when clicking outside
  useEffect(() => {
    if (!bubble.visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        bubbleRef.current &&
        !bubbleRef.current.contains(event.target as Node)
      ) {
        hideBubble();
      }
    };

    // Close on editor changes
    const handleUpdate = () => hideBubble();

    // Delay to prevent the same click from closing the bubble
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    editor.on("update", handleUpdate);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
      editor.off("update", handleUpdate);
    };
  }, [bubble.visible, editor, hideBubble]);

  // Close when editor scrolls
  useEffect(() => {
    if (!bubble.visible) return;

    const scrollContainer = editor.view.dom.closest(".tiptap-editor-content");
    if (!scrollContainer) return;

    const handleScroll = () => hideBubble();
    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [bubble.visible, editor, hideBubble]);

  const handleOpen = useCallback(() => {
    if (bubble.href) {
      window.open(bubble.href, "_blank", "noopener,noreferrer");
    }
    hideBubble();
  }, [bubble.href, hideBubble]);

  const handleEdit = useCallback(() => {
    hideBubble();
    onEdit();
  }, [hideBubble, onEdit]);

  const handleRemove = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    hideBubble();
  }, [editor, hideBubble]);

  if (!bubble.visible) return null;

  return (
    <div
      ref={bubbleRef}
      className="link-bubble"
      style={{ top: bubble.top, left: bubble.left }}
    >
      <a
        href={bubble.href}
        className="link-bubble-url"
        onClick={(e) => {
          e.preventDefault();
          handleOpen();
        }}
        title={bubble.href}
      >
        {bubble.href.length > 40
          ? bubble.href.substring(0, 40) + "..."
          : bubble.href}
      </a>
      <div className="link-bubble-actions">
        <button
          type="button"
          className="link-bubble-btn"
          onClick={handleOpen}
          title="Open link"
        >
          <BsBoxArrowUpRight />
        </button>
        <button
          type="button"
          className="link-bubble-btn"
          onClick={handleEdit}
          title="Edit link"
        >
          <BsPencil />
        </button>
        <button
          type="button"
          className="link-bubble-btn link-bubble-btn-danger"
          onClick={handleRemove}
          title="Remove link"
        >
          <BsTrash />
        </button>
      </div>
    </div>
  );
};

export default LinkBubble;
